import React, { useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, PackageOpen, FileSpreadsheet, FileText, File as FileIcon, CheckCircle2, Trash2, Download, CalendarDays, Users } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { OutlineReview } from './OutlineReview';
import { SortCards } from './SortCards';
import { ProfileGuessBanner } from './ProjectProfile';

const API_URL = process.env.REACT_APP_BACKEND_URL;
export const FILE_KINDS = [
  ['questionnaire', 'Questionnaire (XLS answers + scoring)'], ['narrative', 'Narrative (Word response)'], ['form', 'Client form to complete'],
  ['attachment', 'Attachment / acknowledge'], ['informational', 'Informational only'], ['main_rfp', 'Main RFP document'],
  ['single_document', 'Single document (headings + bullets / Q&A list)'],
];
export const STYLE_LABEL = { standard: 'Standard (annexures + narrative)', single_document: 'Single Document Request', portal_qa: 'Portal Q&A export' };
const SHEET_KINDS = ['questionnaire', 'scale', 'weights', 'picklist', 'form', 'informational'];
const KIND_STYLE = {
  questionnaire: 'bg-[#4ABFCF]/15 text-[#0F2040] border-[#4ABFCF]/40', narrative: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  form: 'bg-amber-50 text-amber-700 border-amber-200', attachment: 'bg-rose-50 text-rose-700 border-rose-200',
  informational: 'bg-slate-100 text-slate-500 border-slate-200', main_rfp: 'bg-[#0F2040] text-white border-[#0F2040]', single_document: 'bg-[#0F2040] text-white border-[#0F2040]',
  scale: 'bg-emerald-50 text-emerald-700 border-emerald-200', weights: 'bg-emerald-50 text-emerald-700 border-emerald-200', picklist: 'bg-slate-100 text-slate-500 border-slate-200',
};
export const KindBadge = ({ kind, testId }) => (
  <Badge variant="outline" className={`text-[9px] capitalize ${KIND_STYLE[kind] || KIND_STYLE.informational}`} data-testid={testId}>{(kind || '').replace('_', ' ')}</Badge>
);
const fileIcon = (name) => /\.xlsx?$/i.test(name) ? FileSpreadsheet : /\.docx$/i.test(name) ? FileText : FileIcon;

export function IntakeDropzone({ project, onStarted }) {
  const [busy, setBusy] = useState(false);
  const [staged, setStaged] = useState(null); // [{file, infoOnly}] waiting for the reviewer to say how each file is used
  const ref = useRef(null);
  const stage = (files) => {
    if (!files?.length) return;
    const list = Array.from(files).map(f => ({ file: f, infoOnly: /\.pdf$/i.test(f.name) && f.size > 4 * 1024 * 1024 }));
    setStaged(list);
    if (ref.current) ref.current.value = '';
  };
  const send = async () => {
    if (!staged?.length) return;
    setBusy(true);
    try {
      const fd = new FormData();
      staged.forEach(x => fd.append('files', x.file));
      fd.append('info_only', JSON.stringify(staged.filter(x => x.infoOnly).map(x => x.file.name)));
      await axios.post(`${API_URL}/api/rfp/response/projects/${project.id}/intake`, fd, { withCredentials: true });
      const n = staged.filter(x => x.infoOnly).length;
      toast.success(`${staged.length} file${staged.length === 1 ? '' : 's'} received — Blueprint is triaging the pack${n ? ` (${n} kept as information only)` : ''}`);
      setStaged(null);
      onStarted?.();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Intake failed'); }
    finally { setBusy(false); }
  };
  return (
    <>
      <label className="inline-flex items-center gap-1.5 text-xs font-medium text-[#0F2040] bg-[#4ABFCF]/15 hover:bg-[#4ABFCF]/25 border border-[#4ABFCF]/40 rounded-md px-3 py-1.5 cursor-pointer"
        title="Drop the whole RFP pack (a ZIP or many files) — Blueprint classifies every file and sheet, detects scoring scales and builds the response shell"
        onDragOver={e => e.preventDefault()} onDrop={e => { e.preventDefault(); stage(e.dataTransfer.files); }} data-testid="rfp-intake-label">
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <PackageOpen className="w-3.5 h-3.5" />}
        Load RFP documents
        <input ref={ref} type="file" multiple accept=".zip,.xlsx,.docx,.pdf,.txt,.md" className="hidden" disabled={busy}
          onChange={e => stage(e.target.files)} data-testid="rfp-intake-input" />
      </label>
      {staged && (
        <div className="w-full mt-2 border border-[#4ABFCF]/40 bg-[#4ABFCF]/5 rounded-md p-3 space-y-2" data-testid="rfp-intake-staged">
          <p className="text-xs text-[#0F2040] font-medium">Before loading — which files are just background reading?</p>
          <p className="text-[10px] text-slate-500">Tick <b>information only</b> for big reference packs (policies, standards, org charts). They are kept for checking answers against but never parsed for questions — so a 600-page PDF can't stall the upload. Large PDFs are pre-ticked; untick any that really hold requirements.</p>
          <ul className="space-y-1">
            {staged.map((x, i) => (
              <li key={i} className="flex items-center gap-2 text-[11px]" data-testid={`rfp-intake-staged-${i}`}>
                {React.createElement(fileIcon(x.file.name), { className: 'w-3.5 h-3.5 text-slate-400 shrink-0' })}
                <span className="text-slate-700 truncate flex-1 min-w-0" title={x.file.name}>{x.file.name}</span>
                <span className="text-slate-400 tabular-nums">{(x.file.size / 1024 / 1024).toFixed(1)} MB</span>
                <label className={`inline-flex items-center gap-1 cursor-pointer rounded-full border px-2 py-0.5 ${x.infoOnly ? 'border-slate-300 bg-slate-100 text-slate-600' : 'border-transparent text-slate-400 hover:text-[#0F2040]'}`}>
                  <input type="checkbox" className="w-3 h-3" checked={x.infoOnly} onChange={e => setStaged(l => l.map((y, k) => (k === i ? { ...y, infoOnly: e.target.checked } : y)))} data-testid={`rfp-intake-staged-${i}-info-only`} />
                  information only
                </label>
              </li>
            ))}
          </ul>
          <div className="flex items-center gap-2">
            <Button size="sm" className="h-7 text-[11px] bg-[#0F2040] hover:bg-[#1A3055] text-white" disabled={busy} onClick={send} data-testid="rfp-intake-staged-load">
              {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <PackageOpen className="w-3 h-3 mr-1" />}Load {staged.length} file{staged.length === 1 ? '' : 's'}
            </Button>
            <button type="button" className="text-[11px] text-slate-400 hover:text-[#0F2040]" onClick={() => setStaged(null)} data-testid="rfp-intake-staged-cancel">cancel</button>
          </div>
        </div>
      )}
    </>
  );
}

export function IntakeReview({ project, onRefresh, onBuilt }) {
  const intake = project.intake;
  const [confirming, setConfirming] = useState(false);
  if (!intake || intake.status === 'confirmed') return null;
  const pid = project.id;
  const override = async (fid, body) => {
    try {
      const r = await axios.put(`${API_URL}/api/rfp/response/projects/${pid}/intake/files/${fid}`, body, { withCredentials: true });
      if (r.data?.taught) {
        const tr = Object.values(body.treatments || {})[0];
        toast.success(`Remembered for ${r.data.taught_client} — the Narrative Library ingest will treat this heading as ${['informational', 'skip'].includes(tr) ? 'an ask (not an answer)' : 'an answer'} next time`, { id: 'sort-taught' });
      }
      onRefresh();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Could not update'); }
  };
  const confirm = async () => {
    setConfirming(true);
    try {
      const r = await axios.post(`${API_URL}/api/rfp/response/projects/${pid}/intake/confirm`, {}, { withCredentials: true });
      toast.success('Response built');
      onBuilt?.(r.data.recap || { sheets: r.data.documents, rows: 0, narratives: 0, sections: 0, reference_files: 0 });
      onRefresh();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Could not confirm'); }
    finally { setConfirming(false); }
  };
  const discard = async () => {
    if (!window.confirm('Discard this intake? Uploaded files and the triage will be removed (existing documents are untouched).')) return;
    try { await axios.delete(`${API_URL}/api/rfp/response/projects/${pid}/intake`, { withCredentials: true }); onRefresh(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Could not discard'); }
  };
  const qSheets = intake.files.reduce((n, f) => n + (f.kind === 'questionnaire' ? f.sheets.filter(s => s.kind === 'questionnaire').length : 0), 0);
  const qRows = intake.files.reduce((n, f) => n + (f.kind === 'questionnaire' ? f.sheets.filter(s => s.kind === 'questionnaire').reduce((m, s) => m + s.question_count, 0) : 0), 0);
  const sdReqs = intake.files.reduce((n, f) => n + (f.kind === 'single_document' && f.outline ? f.outline.stats.requirements : 0), 0);
  const sdMatrix = intake.files.reduce((n, f) => n + (f.kind === 'single_document' && f.outline ? f.outline.stats.matrix_sections : 0), 0);
  return (
    <div className="border border-[#4ABFCF]/40 bg-white rounded-md p-4 space-y-3" data-testid="rfp-intake-review">
      <div className="flex items-center gap-2 flex-wrap">
        <PackageOpen className="w-4 h-4 text-[#4ABFCF]" />
        <span className="text-sm font-semibold text-[#0F2040]">Sort the documents — {intake.files.length} files · pick what each one is for, then build the shell</span>
        <span className="w-full text-[11px] text-slate-500">Blueprint has made a first guess for every file. Check each card — <b>Information only</b>, <b>Narrative to write</b>, <b>Questions to answer</b> or <b>Split it</b> — then press <b>Build the response</b>. Nothing is created until you do.</span>
        {intake.status === 'analysing' ? (
          <Badge variant="outline" className="text-[9px] bg-blue-50 text-blue-700 border-blue-200" data-testid="rfp-intake-progress">
            <Loader2 className="w-2.5 h-2.5 mr-1 animate-spin" />{intake.progress?.label || 'Reviewing with AI…'}
          </Badge>
        ) : (
          <Badge variant="outline" className="text-[9px] bg-emerald-50 text-emerald-700 border-emerald-200" data-testid="rfp-intake-ready">Ready for your review</Badge>
        )}
        <span className="text-[10px] text-slate-500" data-testid="rfp-intake-counts">{sdReqs > 0 ? `${sdReqs} requirements in ${sdMatrix} matrix section${sdMatrix === 1 ? '' : 's'}${qSheets ? ' · ' : ''}` : ''}{(qSheets || !sdReqs) ? `${qSheets} questionnaire sheet${qSheets === 1 ? '' : 's'} · ${qRows} rows to answer` : ''}</span>
        {intake.status === 'review' && (
          <label className="text-[10px] text-slate-500 inline-flex items-center gap-1" title="Response style drives how the shell is built and how the final Word response is laid out">
            style
            <select value={intake.style || (intake.files.some(f => f.kind === 'single_document') ? 'single_document' : 'standard')} onChange={e => override(intake.files[0]?.id, { style: e.target.value })}
              className="h-5 text-[10px] border border-slate-200 rounded px-1 bg-white text-[#0F2040] font-medium" data-testid="rfp-intake-style">
              {Object.entries(STYLE_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </label>
        )}
        <span className="ml-auto flex items-center gap-1.5">
          <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={discard} data-testid="rfp-intake-discard-btn"><Trash2 className="w-3 h-3 mr-1" />Discard</Button>
          <Button size="sm" className="h-7 text-[11px] bg-[#0F2040] hover:bg-[#1A3055] text-white" disabled={intake.status !== 'review' || confirming} onClick={confirm} data-testid="rfp-intake-confirm-btn">
            {confirming ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <CheckCircle2 className="w-3 h-3 mr-1" />}Build the response
          </Button>
        </span>
      </div>
      {intake.ai_note && <p className="text-[10px] text-amber-700 bg-amber-50 border border-amber-100 rounded px-2 py-1">{intake.ai_note}</p>}
      {intake.status === 'review' && <ProfileGuessBanner project={project} onResolved={() => onRefresh()} />}
      {intake.purpose && <p className="text-xs text-slate-600 italic" data-testid="rfp-intake-purpose">{intake.purpose}</p>}
      {(intake.deadlines?.length > 0 || intake.contacts?.length > 0) && (
        <div className="grid md:grid-cols-2 gap-2 text-[11px]">
          {intake.deadlines?.length > 0 && (
            <div className="bg-slate-50 rounded p-2" data-testid="rfp-intake-deadlines">
              <p className="font-semibold text-slate-600 flex items-center gap-1 mb-1"><CalendarDays className="w-3 h-3" />Timeline</p>
              {intake.deadlines.map((d, i) => <p key={i} className="text-slate-600"><span className="font-mono text-[10px] text-[#0F2040]">{d.date}</span> · {d.event}</p>)}
            </div>
          )}
          {intake.contacts?.length > 0 && (
            <div className="bg-slate-50 rounded p-2" data-testid="rfp-intake-contacts">
              <p className="font-semibold text-slate-600 flex items-center gap-1 mb-1"><Users className="w-3 h-3" />Client contacts</p>
              {intake.contacts.map((c, i) => <p key={i} className="text-slate-600">{[c.name, c.role, c.email].filter(Boolean).join(' · ')}</p>)}
              {intake.format_rules?.slice(0, 3).map((r, i) => <p key={`r${i}`} className="text-slate-400">· {r}</p>)}
            </div>
          )}
        </div>
      )}
      <div className="divide-y divide-slate-100 border border-slate-100 rounded-md">
        {intake.files.map(f => <IntakeFileRow key={f.id} f={f} pid={pid} onOverride={override} disabled={intake.status !== 'review'} />)}
      </div>
      {intake.checklist?.length > 0 && (
        <div data-testid="rfp-intake-checklist">
          <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide mb-1">Submission format required by the RFP</p>
          <div className="flex flex-wrap gap-1.5">
            {intake.checklist.map(c => <Badge key={c.id} variant="outline" className="text-[10px] bg-white" title={c.description}>{c.title} <span className="ml-1 text-slate-400">· {c.kind}</span></Badge>)}
          </div>
        </div>
      )}
    </div>
  );
}

function IntakeFileRow({ f, pid, onOverride, disabled }) {
  const Icon = fileIcon(f.filename);
  const shown = f.sheets.filter(s => f.kind === 'questionnaire' ? true : s.kind !== 'informational' && s.kind !== 'picklist');
  return (
    <div className="p-2.5 space-y-1.5" data-testid={`rfp-intake-file-${f.id}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <Icon className="w-3.5 h-3.5 text-slate-400 shrink-0" />
        <span className="text-xs font-medium text-[#0F2040]">{f.filename}</span>
        {f.pending && <Badge variant="outline" className="text-[9px] bg-blue-50 text-blue-700 border-blue-200"><Loader2 className="w-2.5 h-2.5 mr-1 animate-spin" />parsing…</Badge>}
        {f.info_only && <Badge variant="outline" className="text-[9px] bg-slate-100 text-slate-500 border-slate-200" title="You marked this as background reading — stored for checking answers against, not parsed for questions" data-testid={`rfp-intake-info-only-${f.id}`}>information only</Badge>}
        {f.truncated && !f.info_only && <Badge variant="outline" className="text-[9px] bg-amber-50 text-amber-700 border-amber-200" title={f.note} data-testid={`rfp-intake-truncated-${f.id}`}>read {f.pages_read || 0}/{f.pages || '?'} pages</Badge>}
        {f.note && <span className="text-[10px] text-slate-500 truncate max-w-[320px]" title={f.note}>{f.note}</span>}
        {f.error && <Badge variant="outline" className="text-[9px] bg-rose-50 text-rose-700 border-rose-200">{f.error}</Badge>}
        <a href={`${API_URL}/api/rfp/response/projects/${pid}/intake/files/${f.id}/download`} className="ml-auto text-slate-400 hover:text-[#0F2040]" title="Download original" data-testid={`rfp-intake-dl-${f.id}`}><Download className="w-3 h-3" /></a>
      </div>
      {!f.pending && <SortCards f={f} onOverride={onOverride} disabled={disabled} />}
      {f.outline && f.kind === 'single_document' && <OutlineReview f={f} onOverride={onOverride} disabled={disabled} />}
      {f.sections?.length > 0 && f.kind === 'narrative' && (
        <p className="text-[10px] text-slate-500 pl-6">{f.sections.length} sections · {f.sections.slice(0, 5).map(s => s.heading).join(' · ')}{f.sections.length > 5 ? ' …' : ''}</p>
      )}
      {shown.length > 0 && (
        <div className="pl-6 space-y-1">
          {shown.map(s => (
            <div key={s.name} className="flex items-center gap-2 flex-wrap text-[10px]" data-testid={`rfp-intake-sheet-${f.id}-${s.name.replace(/\W+/g, '_')}`}>
              <span className="text-slate-600 font-medium">“{s.name}”</span>
              <select value={s.kind} disabled={disabled} onChange={e => onOverride(f.id, { sheets: { [s.name]: e.target.value } })}
                className="h-5 text-[10px] border border-slate-200 rounded px-1 bg-white text-slate-700">
                {SHEET_KINDS.map(k => <option key={k} value={k}>{k}</option>)}
              </select>
              {s.kind === 'questionnaire' && <span className="text-[#0F2040] font-semibold">{s.question_count} rows</span>}
              {s.scale?.length > 0 && (s.kind === 'questionnaire' || s.kind === 'scale') && (
                <Badge variant="outline" className="text-[9px] bg-emerald-50 text-emerald-700 border-emerald-200" title={s.scale.map(l => `${l.label} — ${l.definition}`).join('\n')}>
                  scale {s.scale.map(l => l.label).join(' / ')}
                </Badge>
              )}
              {s.weights?.length > 0 && (s.kind === 'questionnaire' || s.kind === 'weights') && (
                <Badge variant="outline" className="text-[9px] bg-emerald-50 text-emerald-700 border-emerald-200" title={s.weights.map(w => `${w.category}: ${Math.round(w.weight * 100)}%`).join('\n')}>
                  {s.weights.length} category weights
                </Badge>
              )}
              {s.kind !== 'questionnaire' && s.sample?.[0] && <span className="text-slate-400 truncate max-w-[260px]">{s.sample[0]}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function IntakeSummaryDialog({ project, open, onOpenChange }) {
  const intake = project.intake;
  if (!intake) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="rfp-intake-summary-dialog">
        <DialogHeader><DialogTitle className="text-sm flex items-center gap-2"><PackageOpen className="w-4 h-4 text-[#4ABFCF]" />RFP pack — {intake.files.length} files</DialogTitle></DialogHeader>
        {intake.purpose && <p className="text-xs text-slate-600 italic">{intake.purpose}</p>}
        <div className="divide-y divide-slate-100 border border-slate-100 rounded-md">
          {intake.files.map(f => (
            <div key={f.id} className="p-2 flex items-center gap-2 text-xs">
              <KindBadge kind={f.kind} />
              <span className="text-[#0F2040]">{f.filename}</span>
              <a href={`${API_URL}/api/rfp/response/projects/${project.id}/intake/files/${f.id}/download`} className="ml-auto text-slate-400 hover:text-[#0F2040]" title="Download original"><Download className="w-3 h-3" /></a>
            </div>
          ))}
        </div>
        {intake.deadlines?.length > 0 && <div className="text-[11px] text-slate-600">{intake.deadlines.map((d, i) => <p key={i}><span className="font-mono text-[#0F2040]">{d.date}</span> · {d.event}</p>)}</div>}
      </DialogContent>
    </Dialog>
  );
}
